import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, Users, Phone, Mail, Globe, Pencil, Trash2, Eye, Briefcase, FileText, MessageSquare, X,  Upload, Camera, CreditCard, ExternalLink, MessageCircle, Copy, Check, Send } from 'lucide-react';
import {
  Card, Button, Input, Select, Badge, EmptyState, Modal, Label, Textarea, ConfirmDeleteModal,
  SkeletonListRows, HelpTooltip,
} from '../components/ui';
import FirstVisitTips from '../components/onboarding/FirstVisitTips';
import SwipeableRow from '../components/SwipeableRow';
import { useFirm } from '../contexts/FirmContext';
import { useRoute } from '../lib/router';
import { listContacts, createContact, updateContact, deleteContact, deletePortalUser, invitePortalClient, listMatters, listInvoices, uploadGhanaCard } from '../lib/db';
import { CONTACT_TYPES, GHANA_REGIONS } from '../lib/constants';
import { fullName, formatDate, formatMoney } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

// ── Portal preview modal ──────────────────────────────────────────────────────
function PortalPreviewModal({ contact, firmId, onClose }) {
  const [matters,  setMatters]  = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [view,     setView]     = useState('dashboard'); // 'dashboard' | 'matters' | 'invoices'

  useEffect(() => {
    (async () => {
      const [{ data: m }, { data: i }] = await Promise.all([
        listMatters(firmId).then ? listMatters(firmId) : { data: [] },
        listInvoices(firmId),
      ]);
      setMatters((m || []).filter((x) => x.client_id === contact.id));
      setInvoices((i || []).filter((x) => x.client_id === contact.id));
      setLoading(false);
    })();
  }, [contact.id, firmId]);

  const openMatters    = matters.filter((m) => ['open','in_progress'].includes(m.status));
  const unpaidInvoices = invoices.filter((i) => ['sent','overdue','partial'].includes(i.status));
  const totalDue       = unpaidInvoices.reduce((s, i) => s + Number(i.total || 0), 0);

  const STATUS_COLOR = {
    open:        'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    closed:      'bg-slate-100 text-slate-600',
  };
  const INV_COLOR = {
    draft: 'bg-slate-100 text-slate-500',
    sent:  'bg-blue-100 text-blue-700',
    paid:  'bg-emerald-100 text-emerald-700',
    overdue: 'bg-red-100 text-red-700',
    partial: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Portal shell */}
      <div className="relative z-10 flex w-full max-w-4xl mx-auto my-6 rounded-2xl overflow-hidden shadow-2xl flex-col bg-slate-50 dark:bg-slate-900">

        {/* Portal top bar */}
        <div className="flex items-center justify-between px-6 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Briefcase size={13} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">Client Portal</p>
              <p className="text-xs text-slate-400">Previewing as {fullName(contact) || contact.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium mr-2">Admin Preview</span>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Portal nav */}
        <div className="flex gap-1 px-6 py-2 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          {[
            { key: 'dashboard', label: 'Dashboard',  icon: Briefcase },
            { key: 'matters',   label: 'Case Files', icon: Briefcase },
            { key: 'invoices',  label: 'Invoices',   icon: FileText },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setView(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                view === key
                  ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}>
              <Icon size={13} />{label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-16 text-slate-400 text-sm">Loading client data…</div>
          ) : view === 'dashboard' ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Welcome back, {contact.first_name || 'there'}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">Here's a summary of your case files and account.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Active cases',    value: openMatters.length,    color: 'bg-indigo-500', icon: Briefcase },
                  { label: 'Unpaid invoices', value: unpaidInvoices.length, color: 'bg-amber-500',  icon: FileText },
                  { label: 'Amount due',      value: formatMoney(totalDue), color: 'bg-red-500',    icon: MessageSquare },
                ].map(({ label, value, color, icon: Icon }) => (
                  <div key={label} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                    <div className={`inline-flex p-2 rounded-lg mb-2 ${color}`}>
                      <Icon size={15} className="text-white" />
                    </div>
                    <p className="text-xl font-bold text-slate-900 dark:text-white">{value}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {matters.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Your case files</h2>
                  <div className="space-y-2">
                    {matters.slice(0, 4).map((m) => (
                      <div key={m.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{m.title}</p>
                          <p className="text-xs text-slate-400">{m.matter_number}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLOR[m.status] || STATUS_COLOR.open}`}>
                          {m.status?.replace('_',' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : view === 'matters' ? (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Case Files</h2>
              {matters.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10">No case files assigned to this client.</p>
              ) : matters.map((m) => (
                <div key={m.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 dark:text-slate-200">{m.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{m.matter_number} · {m.practice_area || '—'}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${STATUS_COLOR[m.status] || STATUS_COLOR.open}`}>
                    {m.status?.replace('_',' ')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Invoices</h2>
              {invoices.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10">No invoices for this client.</p>
              ) : invoices.map((inv) => (
                <div key={inv.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 dark:text-slate-200">{inv.invoice_number}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Due {formatDate(inv.due_date)}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${INV_COLOR[inv.status] || INV_COLOR.sent}`}>
                      {inv.status}
                    </span>
                    <span className="font-semibold font-mono text-sm text-slate-700 dark:text-slate-300">{formatMoney(inv.total || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const blank = {
  first_name: '', last_name: '', organization: '',
  email: '', phone: '', contact_type: 'client',
  address: '', city: '', region: '', zip: '',
  ghana_card_number: '', ghana_card_url: '',
  notes: '',
};

export default function ContactsPage() {
  const { firmId, refreshSignal, refresh: refreshFirm, loading: firmLoading } = useFirm();
  const [, navigate] = useRoute();
  const log = useActivityLogger();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editContact, setEditContact] = useState(null); // contact being edited
  const [form, setForm] = useState(blank);
  const [formErrors, setFormErrors] = useState({});
  const [saving,    setSaving]    = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [inviting,        setInviting]        = useState(null); // contactId being invited (email send)
  const [inviteTarget,    setInviteTarget]    = useState(null); // contact for invite modal
  const [inviteLinkCopied,setInviteLinkCopied]= useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [previewContact,  setPreviewContact]  = useState(null);
  const [cardFile,  setCardFile]  = useState(null);   // pending File to upload
  const [cardPreview, setCardPreview] = useState(''); // object URL for preview
  const cardUploadRef = useRef(null);  // file picker (no capture)
  const cardCameraRef = useRef(null);  // camera capture

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await listContacts(firmId);
        setContacts(data || []);
      } catch (err) {
        console.error('ContactsPage fetch error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [firmId, refreshSignal]);

  const filtered = useMemo(() => contacts
    .filter((c) => !typeFilter || c.contact_type === typeFilter)
    .filter((c) => {
      if (!q) return true;
      const s = q.toLowerCase();
      return [fullName(c), c.email, c.phone, c.organization]
        .some((v) => (v || '').toLowerCase().includes(s));
    }), [contacts, q, typeFilter]);

  const portalInviteLink = (contact) =>
    `${window.location.origin}/#/portal-invite/${contact?.portal_invite_token || contact?.id}`;

  const sendEmailInvite = async (contact) => {
    if (!contact.email) { toast.warning('This contact has no email address. Add one before inviting.'); return; }
    setInviting(contact.id);
    const { error } = await invitePortalClient(contact.id);
    setInviting(null);
    if (error) {
      toast.error('Invite failed: ' + error.message);
    } else {
      setContacts((prev) => prev.map((c) => c.id === contact.id ? { ...c, portal_enabled: true, portal_invited_at: new Date().toISOString() } : c));
      setInviteTarget(null);
    }
  };

  const sendWhatsAppInvite = (contact) => {
    const phone = (contact.phone || '').replace(/\D/g, '');
    const clientName = fullName(contact) || contact.organization || 'there';
    const link = portalInviteLink(contact);
    const text = encodeURIComponent(
      `Hi ${clientName},\n\nYou've been invited to access your case files and invoices on our secure client portal.\n\nClick the link below to set up your account:\n${link}\n\nIf you have any questions, don't hesitate to reach out.`
    );
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const copyInviteLink = (contact) => {
    navigator.clipboard.writeText(portalInviteLink(contact));
    setInviteLinkCopied(true);
    setTimeout(() => setInviteLinkCopied(false), 2500);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.first_name.trim()) errors.first_name = 'First name is required.';
    if (!form.last_name.trim())  errors.last_name  = 'Last name is required.';
    if (!form.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
    if (!form.phone.trim())   errors.phone   = 'Phone number is required.';
    if (!form.address.trim()) errors.address = 'Address is required.';
    // Ghana card: if either field is touched, both are required
    const hasCardNumber = !!form.ghana_card_number.trim();
    const hasCardImage  = !!(cardFile || form.ghana_card_url);
    if (hasCardNumber || hasCardImage) {
      if (!hasCardNumber) errors.ghana_card_number = 'Card number is required when uploading a card.';
      if (!hasCardImage)  errors.ghana_card_image  = 'Card image is required when entering a card number.';
    }
    return errors;
  };

  const openEdit = (contact) => {
    setEditContact(contact);
    setForm({
      first_name:         contact.first_name         || '',
      last_name:          contact.last_name          || '',
      organization:       contact.organization       || '',
      email:              contact.email              || '',
      phone:              contact.phone              || '',
      contact_type:       contact.contact_type       || 'client',
      address:            contact.address            || '',
      city:               contact.city               || '',
      region:             contact.region             || '',
      zip:                contact.zip                || '',
      ghana_card_number:  contact.ghana_card_number  || '',
      ghana_card_url:     contact.ghana_card_url     || '',
      notes:              contact.notes              || '',
    });
    setCardFile(null);
    setCardPreview(contact.ghana_card_url || '');
    setFormErrors({});
  };

  const closeForm = () => {
    setShowNew(false);
    setEditContact(null);
    setForm(blank);
    setFormErrors({});
    setSubmitError('');
    setCardFile(null);
    setCardPreview('');
  };

  const handleCardFile = (file) => {
    if (!file) return;
    setCardFile(file);
    setCardPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    setSubmitError('');

    // If firm hasn't loaded yet, trigger a silent refresh and ask the user to retry.
    // We keep the form open so no data is lost.
    if (!firmId) {
      refreshFirm(true);
      setSubmitError('Your firm data is still loading. Please wait a moment and try again.');
      return;
    }

    setSaving(true);
    try {
      if (editContact) {
        // Update existing contact
        const { data, error } = await updateContact(editContact.id, form);
        if (error) { setSubmitError(error.message); return; }
        let saved = data;
        // Upload Ghana card if a new file was selected
        if (cardFile) {
          const { url, error: upErr } = await uploadGhanaCard(firmId, 'contacts', saved.id, cardFile);
          if (upErr) { setSubmitError('Contact saved but card upload failed: ' + upErr.message); }
          else if (url) {
            await updateContact(saved.id, { ghana_card_url: url });
            saved = { ...saved, ghana_card_url: url };
          }
        }
        log('contact', saved.id, 'updated', `Contact "${fullName(saved) || saved.organization}" updated`);
        setContacts((prev) => prev.map((c) => c.id === editContact.id ? { ...c, ...saved } : c));
        closeForm();
      } else {
        // Create new contact
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out after 20s. Check your network or Supabase project status.')), 20000)
        );
        const { data, error } = await Promise.race([createContact(firmId, form), timeout]);
        if (error) { setSubmitError(error.message); return; }
        let saved = data;
        // Upload Ghana card if provided
        if (cardFile) {
          const { url, error: upErr } = await uploadGhanaCard(firmId, 'contacts', saved.id, cardFile);
          if (upErr) { setSubmitError('Contact saved but card upload failed: ' + upErr.message); }
          else if (url) {
            await updateContact(saved.id, { ghana_card_url: url });
            saved = { ...saved, ghana_card_url: url };
          }
        }
        log('contact', saved.id, 'created', `Contact "${fullName(saved) || saved.organization}" added`);
        setContacts((prev) => [saved, ...prev]);
        closeForm();
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async () => {
    // If the contact has a portal account, clean up auth.users first.
    if (deletingContact.portal_user_id) {
      await deletePortalUser(deletingContact.id);
    }
    const { error } = await deleteContact(deletingContact.id);
    if (error) { toast.error(error.message); return; }
    log('contact', deletingContact.id, 'deleted', 'Contact removed');
    setContacts((prev) => prev.filter((c) => c.id !== deletingContact.id));
    setDeletingContact(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {contacts.length} total · clients, opposing counsel, experts, witnesses
          </p>
        </div>
        <Button icon={Plus} onClick={() => setShowNew(true)}>New contact</Button>
      </div>

      <FirstVisitTips
        pageKey="contacts"
        title="Everyone connected to your work"
        tips={[
          'Clients can be invited to a portal where they download invoices and exchange documents with you.',
          'Tag contacts as Opposing counsel, Witness, Expert, etc. — they show up on the right matter automatically.',
          'You can\'t bill non-clients (witnesses, opposing counsel), but you can attach them to matters for record-keeping.',
        ]}
      />

      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input placeholder="Search by name, email, org…" value={q}
              onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            options={[{ value: '', label: 'All types' }, ...CONTACT_TYPES]} />
        </div>
        {loading ? (
          <SkeletonListRows rows={6} withAvatar />
        ) : !filtered.length ? (
          contacts.length === 0 ? (
            <EmptyState
              icon={Users}
              title="Add your first contact"
              description="Contacts are your clients, opposing counsel, experts, witnesses — everyone connected to a case. Add one to start, or load sample contacts from the dashboard checklist."
              action={<Button icon={Plus} onClick={() => setShowNew(true)}>New contact</Button>}
            />
          ) : (
            <EmptyState
              icon={Users}
              title="No contacts match your filters"
              description="Try clearing the search or type filter above."
              action={<Button icon={Plus} onClick={() => setShowNew(true)}>New contact</Button>}
            />
          )
        ) : (
          <>
            {/* Mobile card list */}
            <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((c) => {
                const type = CONTACT_TYPES.find((t) => t.value === c.contact_type);
                return (
                  <li key={c.id}>
                    <SwipeableRow
                      onTap={() => navigate(`/contacts/${c.id}`)}
                      rightActions={[
                        { label: 'Edit',   color: 'indigo', icon: Pencil, onAction: () => openEdit(c) },
                        { label: 'Delete', color: 'red',    icon: Trash2, onAction: () => setDeletingContact(c) },
                      ]}
                    >
                      <div className="flex items-center gap-3 px-4 py-3 active:bg-slate-50 dark:active:bg-slate-800">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {(fullName(c) || c.organization || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{fullName(c) || c.organization || '—'}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {[type?.label, c.email || c.phone].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </div>
                    </SwipeableRow>
                  </li>
                );
              })}
            </ul>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Name</th>
                    <th className="text-left px-4 py-2 font-medium">Type</th>
                    <th className="text-left px-4 py-2 font-medium">Email</th>
                    <th className="text-left px-4 py-2 font-medium">Phone</th>
                    <th className="text-left px-4 py-2 font-medium">Added</th>
                    <th className="text-left px-4 py-2 font-medium">Portal</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map((c) => {
                    const type = CONTACT_TYPES.find((t) => t.value === c.contact_type);
                    return (
                      <tr key={c.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition" onClick={() => navigate(`/contacts/${c.id}`)}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-indigo-700 dark:text-indigo-400 hover:underline">{fullName(c) || c.organization || '—'}</p>
                          {c.organization && fullName(c) && <p className="text-xs text-slate-500">{c.organization}</p>}
                        </td>
                        <td className="px-4 py-3"><Badge>{type?.label || c.contact_type}</Badge></td>
                        <td className="px-4 py-3 text-slate-500" onClick={(e) => e.stopPropagation()}>
                          {c.email ? <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail size={12} /> {c.email}</a> : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {c.phone ? <span className="inline-flex items-center gap-1"><Phone size={12} /> {c.phone}</span> : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{formatDate(c.created_at)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {c.contact_type === 'client' ? (
                            c.portal_enabled ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium"><Globe size={11} /> Active</span>
                                <button onClick={() => setPreviewContact(c)} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium" title="Preview portal"><Eye size={11} /> Preview</button>
                                <button onClick={() => { setInviteTarget(c); setInviteLinkCopied(false); }} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600 font-medium">↺ Re-invite</button>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" icon={Globe} onClick={() => { setInviteTarget(c); setInviteLinkCopied(false); }}>Invite</Button>
                            )
                          ) : (
                            <span className="text-slate-300 dark:text-slate-600 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" icon={Pencil} onClick={() => openEdit(c)}>Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeletingContact(c)}><Trash2 size={13} className="text-red-400" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <Modal open={showNew || !!editContact} onClose={closeForm} title={editContact ? `Edit — ${fullName(editContact) || editContact.organization || 'Contact'}` : 'New contact'} size="lg" footer={
        <>
          <Button variant="ghost" onClick={closeForm}>Cancel</Button>
          <Button loading={saving} disabled={firmLoading} onClick={submit}>{editContact ? 'Save changes' : 'Create'}</Button>
        </>
      }>
        {submitError && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {submitError}
          </div>
        )}
        <form onSubmit={submit} className="space-y-5" noValidate>
          {/* Basic info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>First name <span className="text-red-500">*</span></Label>
              <Input value={form.first_name} onChange={(e) => { setForm({ ...form, first_name: e.target.value }); setFormErrors((p) => ({ ...p, first_name: undefined })); }} className={formErrors.first_name ? 'ring-red-400' : ''} />
              {formErrors.first_name && <p className="text-xs text-red-600 mt-1">{formErrors.first_name}</p>}
            </div>
            <div>
              <Label>Last name <span className="text-red-500">*</span></Label>
              <Input value={form.last_name} onChange={(e) => { setForm({ ...form, last_name: e.target.value }); setFormErrors((p) => ({ ...p, last_name: undefined })); }} className={formErrors.last_name ? 'ring-red-400' : ''} />
              {formErrors.last_name && <p className="text-xs text-red-600 mt-1">{formErrors.last_name}</p>}
            </div>
            <div className="sm:col-span-2"><Label>Organization</Label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} /></div>
            <div>
              <Label>
                Type
                <HelpTooltip>
                  <strong>Client</strong> — you can open matters and send invoices.<br/>
                  <strong>Opposing counsel</strong>, <strong>witness</strong>, <strong>expert</strong>, <strong>court</strong> — referenced from matters but not billed.<br/>
                  Type can be changed later.
                </HelpTooltip>
              </Label>
              <Select options={CONTACT_TYPES} value={form.contact_type} onChange={(e) => setForm({ ...form, contact_type: e.target.value })} />
            </div>
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setFormErrors((p) => ({ ...p, email: undefined })); }} className={formErrors.email ? 'ring-red-400' : ''} />
              {formErrors.email && <p className="text-xs text-red-600 mt-1">{formErrors.email}</p>}
            </div>
            <div>
              <Label>Phone <span className="text-red-500">*</span></Label>
              <Input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => { setForm({ ...form, phone: e.target.value }); setFormErrors((p) => ({ ...p, phone: undefined })); }} className={formErrors.phone ? 'ring-red-400' : ''} />
              {formErrors.phone && <p className="text-xs text-red-600 mt-1">{formErrors.phone}</p>}
            </div>
            <div>
              <Label>Address <span className="text-red-500">*</span></Label>
              <Input value={form.address} onChange={(e) => { setForm({ ...form, address: e.target.value }); setFormErrors((p) => ({ ...p, address: undefined })); }} className={formErrors.address ? 'ring-red-400' : ''} />
              {formErrors.address && <p className="text-xs text-red-600 mt-1">{formErrors.address}</p>}
            </div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div>
              <Label>Region</Label>
              <Select
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                options={[{ value: '', label: 'Select region…' }, ...GHANA_REGIONS.map((r) => ({ value: r, label: r }))]}
              />
            </div>
            <div><Label>Postal code</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
          </div>

          {/* Ghana Card */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><CreditCard size={13} /> Ghana Card</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Ghana Card Number {(form.ghana_card_number || cardFile || form.ghana_card_url) && <span className="text-red-500">*</span>}</Label>
                <Input
                  value={form.ghana_card_number}
                  onChange={(e) => { setForm({ ...form, ghana_card_number: e.target.value }); setFormErrors((p) => ({ ...p, ghana_card_number: undefined })); }}
                  placeholder="GHA-XXXXXXXXX-X"
                  className={formErrors.ghana_card_number ? 'ring-red-400' : ''}
                />
                {formErrors.ghana_card_number && <p className="text-xs text-red-600 mt-1">{formErrors.ghana_card_number}</p>}
              </div>
            </div>
            {/* Upload / scan */}
            <div>
              <Label>Card image</Label>
              {/* Two separate hidden inputs — browsers ignore runtime capture attribute changes */}
              <input ref={cardUploadRef} type="file" accept="image/*,application/pdf" className="hidden"
                onChange={(e) => { handleCardFile(e.target.files?.[0]); e.target.value = ''; }} />
              <input ref={cardCameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { handleCardFile(e.target.files?.[0]); e.target.value = ''; }} />

              {cardPreview ? (
                <div className="mt-1 flex items-start gap-3">
                  {(cardFile || cardPreview.match(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i)) ? (
                    <img
                      src={cardPreview}
                      alt="Ghana card"
                      className="h-24 w-auto rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                    />
                  ) : (
                    <a href={cardPreview} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
                      <ExternalLink size={13} /> View current card
                    </a>
                  )}
                  <div className="flex flex-col gap-1.5 mt-1">
                    <button type="button" onClick={() => cardUploadRef.current?.click()} className="text-xs text-indigo-600 hover:underline text-left">Replace image</button>
                    <button type="button" onClick={() => { setCardFile(null); setCardPreview(''); setForm((f) => ({ ...f, ghana_card_url: '' })); }} className="text-xs text-red-500 hover:underline text-left">Remove</button>
                  </div>
                </div>
              ) : (
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    onClick={() => cardUploadRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                  >
                    <Upload size={14} /> Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => cardCameraRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                  >
                    <Camera size={14} /> Scan / take photo
                  </button>
                </div>
              )}
              {formErrors.ghana_card_image && <p className="text-xs text-red-600 mt-2">{formErrors.ghana_card_image}</p>}
            </div>
          </div>

          {/* Notes */}
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </form>
      </Modal>

      {/* Delete contact modal */}
      <ConfirmDeleteModal
        open={!!deletingContact}
        onClose={() => setDeletingContact(null)}
        loading={false}
        title="Delete contact"
        description={<>Are you sure you want to delete <strong>{fullName(deletingContact) || deletingContact?.organization}</strong>? This will remove all associated data.</>}
        onConfirm={handleDeleteContact}
      />

      {/* Portal preview */}
      {previewContact && (
        <PortalPreviewModal
          contact={previewContact}
          firmId={firmId}
          onClose={() => setPreviewContact(null)}
        />
      )}

      {/* Portal Invite Modal */}
      <Modal open={!!inviteTarget} title="Invite to Client Portal" onClose={() => { setInviteTarget(null); setInviteLinkCopied(false); }}>
        {inviteTarget && (
          <div className="space-y-5">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Invite <span className="font-semibold text-slate-700 dark:text-slate-200">{fullName(inviteTarget) || inviteTarget.organization}</span> to access their case files, invoices, and receipts on the secure client portal.
            </p>

            {/* Email */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                  <Send size={14} className="text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Email invite</p>
                  <p className="text-xs text-slate-400">Sends a secure sign-up link to {inviteTarget.email || 'their email'}</p>
                </div>
              </div>
              {inviteTarget.email ? (
                <button
                  onClick={() => sendEmailInvite(inviteTarget)}
                  disabled={inviting === inviteTarget.id}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition"
                >
                  {inviting === inviteTarget.id ? 'Sending…' : <><Send size={14} /> Send email invite</>}
                </button>
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                  ⚠ No email address on file. Edit the contact to add one.
                </p>
              )}
            </div>

            {/* WhatsApp */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={14} className="text-[#25D366]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">WhatsApp</p>
                  <p className="text-xs text-slate-400">Opens WhatsApp with a pre-written invite message</p>
                </div>
              </div>
              <button
                onClick={() => sendWhatsAppInvite(inviteTarget)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-medium transition"
              >
                <MessageCircle size={14} /> Send via WhatsApp
              </button>
              {!inviteTarget.phone && (
                <p className="text-xs text-amber-600 dark:text-amber-400">⚠ No phone on file — WhatsApp will open without a pre-filled recipient.</p>
              )}
            </div>

            {/* Copy link */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Copy size={14} className="text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Copy invite link</p>
                  <p className="text-xs text-slate-400">Share manually via any channel</p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 text-xs font-mono bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-500 truncate select-all">
                  {portalInviteLink(inviteTarget)}
                </div>
                <button
                  onClick={() => copyInviteLink(inviteTarget)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition flex-shrink-0 ${
                    inviteLinkCopied
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                >
                  {inviteLinkCopied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
